import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptionByToken } from '../subscriptions/core/subscriptionService';
import { WhiteLabelProvider } from '../subscriptions/components/WhiteLabelProvider';
import { useThemeMode } from '../../../_metronic/partials';
import { getFileBase64Public } from '../../services/files';
import { toAbsoluteUrl } from '../../../_metronic/helpers';

export const CheckoutSuccess: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useThemeMode();

  const paymentMethodFromState = (location.state as any)?.paymentMethod;

  const [logoSrc, setLogoSrc] = useState<string>('');
  const [logoLoading, setLogoLoading] = useState(true);

  const { data: subscription, isLoading, isError } = useQuery({
    queryKey: ['checkoutSuccess', token],
    queryFn: () => getSubscriptionByToken(token!),
    enabled: !!token,
  });

  const company = (subscription as any)?.unitBranch?.company;

  useEffect(() => {
    if (company) {
      const imgKey = mode === 'dark' && company.image_dark ? company.image_dark : company.image;
      if (imgKey) {
        if (imgKey.includes('/') || imgKey.startsWith('data:')) {
          setLogoSrc(imgKey);
          setLogoLoading(false);
        } else {
          setLogoLoading(true);
          getFileBase64Public(imgKey).then((res: any) => {
            if (res && res.base64) {
              setLogoSrc(`data:image/png;base64,${res.base64}`);
            } else {
              setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
            }
            setLogoLoading(false);
          }).catch(() => {
            setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
            setLogoLoading(false);
          });
        }
      } else {
        setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
        setLogoLoading(false);
      }
    } else {
      if (subscription) {
        setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
        setLogoLoading(false);
      }
    }
  }, [subscription, mode]);

  if (isLoading) {
    return (
      <div className='d-flex flex-column flex-root bg-body h-100 flex-center' style={{ minHeight: '100vh' }}>
        <span className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </span>
      </div>
    );
  }

  if (isError || !subscription) {
    return (
      <div className='d-flex flex-column flex-root bg-body h-100 flex-center' style={{ minHeight: '100vh' }}>
        <h2 className="text-danger">Link de acesso inválido ou expirado.</h2>
        <button className='btn btn-primary mt-5' onClick={() => navigate('/')}>Voltar ao Início</button>
      </div>
    );
  }

  const charges = (subscription as any).charges || [];
  const activeCharge = charges.length > 0 ? charges[charges.length - 1] : null;

  const hasCardToken = !!(subscription as any)?.credit_card_token;
  const isCardPayment = paymentMethodFromState === 'CARTAO' || hasCardToken;

  const cycleMultipliers: Record<string, number> = { MENSAL: 1, TRIMESTRAL: 3, SEMESTRAL: 6, ANUAL: 12 };
  const mult = cycleMultipliers[subscription?.cycle || 'MENSAL'] || 1;
  const recurringTotal = (Number(subscription?.monthly_value) || 0) * mult;
  const totalInstallments = Number(subscription?.total_installments) || 0;
  const installmentValue = totalInstallments > 1
    ? (Number(subscription?.monthly_value) || 0)
    : recurringTotal;

  const formatCurrency = (val: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const valueFormatted = activeCharge ? formatCurrency(activeCharge.gross_value) : formatCurrency(recurringTotal);
  const dateFormatted = activeCharge && activeCharge.due_date ? new Date(activeCharge.due_date).toLocaleDateString('pt-BR') : 'Hoje';

  const appliedCoupon = (subscription as any)?.coupon;
  let discountAmount = 0;
  if (appliedCoupon) {
    const baseForDiscount = recurringTotal + (Number(subscription?.adhesion_fee) || 0);
    if (appliedCoupon.discount_type === 'PERCENTAGE') {
      discountAmount = baseForDiscount * (appliedCoupon.discount_value / 100);
    } else {
      discountAmount = Math.min(baseForDiscount, appliedCoupon.discount_value);
    }
  }

  return (
    <WhiteLabelProvider company={company}>
      <div className='d-flex flex-column flex-root bg-body h-100' style={{ minHeight: '100vh' }}>
        <div className='d-flex flex-column flex-center w-100 p-10'>
          <div className='mb-10'>
            {logoLoading ? (
              <span className="spinner-border spinner-border-sm text-muted"></span>
            ) : (
              logoSrc && <img alt='Logo' src={logoSrc} className='h-40px' />
            )}
          </div>
          <div className='card card-flush shadow-sm mw-600px w-100 text-center'>
            <div className='card-body py-15'>
            <div className='mb-8'>
              <span className='svg-icon svg-icon-5x svg-icon-success'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <rect opacity='0.3' x='2' y='2' width='20' height='20' rx='10' fill='currentColor' />
                  <path d='M10.4343 12.4343L8.75 10.75C8.33579 10.3358 7.66421 10.3358 7.25 10.75C6.83579 11.1642 6.83579 11.8358 7.25 12.25L10.2929 15.2929C10.6834 15.6834 11.3166 15.6834 11.7071 15.2929L17.25 9.75C17.6642 9.33579 17.6642 8.66421 17.25 8.25C16.8357 7.83579 16.1642 7.83579 15.75 8.25L11.5657 12.4343C11.2533 12.7467 10.7467 12.7467 10.4343 12.4343Z' fill='currentColor' />
                </svg>
              </span>
            </div>

            <h1 className='fw-bolder text-gray-900 mb-5'>Assinatura Confirmada!</h1>
            <div className='fw-bold fs-5 text-gray-600 mb-10'>
              {isCardPayment
                ? 'Sua assinatura foi processada e o pagamento com cartão de crédito foi confirmado.'
                : 'Sua assinatura foi processada com sucesso. A primeira cobrança foi gerada e já está disponível para pagamento.'}
            </div>

            <div className='bg-light-primary rounded border-primary border border-dashed p-8 mb-10 text-start'>
              <h3 className='fw-bolder text-gray-800 mb-4'>Detalhes do Pagamento</h3>
              <div className='row g-5 mb-4'>
                <div className='col-sm-6'>
                  <div className='fw-bold text-gray-600 mb-1'>Vencimento</div>
                  <div className='fw-bolder fs-5 text-gray-800'>{dateFormatted}</div>
                </div>
                <div className='col-sm-6'>
                  <div className='fw-bold text-gray-600 mb-1'>
                    {totalInstallments > 1 ? `Parcela 1/${totalInstallments}` : 'Total da 1ª Parcela'}
                  </div>
                  <div className='fw-bolder fs-5 text-gray-800'>{valueFormatted}</div>
                </div>
              </div>
              <div className='separator border-primary border-dashed mb-4 opacity-25'></div>
              <div className='d-flex flex-column gap-2'>
                <div className='d-flex justify-content-between'>
                  <span className='fw-bold text-gray-600'>
                    {totalInstallments > 1 ? `Mensalidade (${totalInstallments}x)` : 'Mensalidade (Base)'}
                  </span>
                  <span className='fw-bolder text-gray-800'>
                    {totalInstallments > 1 ? formatCurrency(installmentValue) : formatCurrency(recurringTotal)}
                  </span>
                </div>
                {Number(subscription?.adhesion_fee) > 0 && (
                  <div className='d-flex justify-content-between'>
                    <span className='fw-bold text-success'>Taxa de Adesão (Única)</span>
                    <span className='fw-bolder text-success'>+ {formatCurrency(Number(subscription.adhesion_fee))}</span>
                  </div>
                )}
                {appliedCoupon && (
                  <div className='d-flex justify-content-between'>
                    <span className='fw-bold text-danger'>Desconto ({appliedCoupon.code})</span>
                    <span className='fw-bolder text-danger'>- {formatCurrency(discountAmount)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex justify-content-center'>
              {isCardPayment ? (
                <div className='d-flex align-items-center bg-light-success rounded p-4'>
                  <i className='bi bi-check2-circle text-success fs-2 me-3'></i>
                  <span className='text-success fw-bolder fs-6'>Pagamento aprovado no cartão de crédito</span>
                </div>
              ) : (
                <button 
                  className='btn btn-primary fw-bolder px-8 py-3'
                  onClick={() => activeCharge?.invoice_link ? window.open(activeCharge.invoice_link, '_blank') : null}
                  disabled={!activeCharge?.invoice_link}
                >
                  <i className='bi bi-credit-card fs-3 me-2'></i> Efetuar Pagamento
                </button>
              )}
            </div>

          </div>
          </div>
        </div>
      </div>
    </WhiteLabelProvider>
  );
};
